<template>
  <v-container fluid class="estado-cuentas pa-4 pa-md-6">
    <!-- Header -->
    <div class="page-header d-flex flex-wrap align-center mb-6">
      <div class="d-flex align-center">
        <div class="page-header__icon">
          <v-icon color="primary" size="28">mdi-bank-outline</v-icon>
        </div>
        <div>
          <h1 class="page-header__title">Estado de Cuenta Bancos</h1>
          <p class="page-header__subtitle mb-0">
            Resumen de ingresos y egresos por banco y escuela
          </p>
        </div>
      </div>
      <v-spacer></v-spacer>
      <div class="page-header__actions d-flex flex-wrap mt-3 mt-md-0">
        <v-btn
          :color="vistaActiva === 'ingresosEgresos' ? 'teal darken-1' : 'teal'"
          :outlined="vistaActiva !== 'ingresosEgresos'"
          small
          depressed
          class="mr-2 mb-2 mb-sm-0 rounded-lg"
          @click="toggleVistaIngresosEgresos"
        >
          <v-icon left small>mdi-table-pivot</v-icon>
          Ingresos vs Egresos
        </v-btn>
        <v-btn
          outlined
          small
          color="error"
          class="mr-2 mb-2 mb-sm-0 rounded-lg"
          @click="abrirArchivosFaltantes"
        >
          <v-icon left small>mdi-file-alert-outline</v-icon>
          Faltantes
        </v-btn>
        <v-btn
          outlined
          small
          color="indigo"
          class="mr-2 mb-2 mb-sm-0 rounded-lg"
          @click="abrirArchivosSubidos"
        >
          <v-icon left small>mdi-file-download-outline</v-icon>
          Archivos subidos
        </v-btn>
        <v-btn
          outlined
          small
          color="orange darken-2"
          class="mr-2 mb-2 mb-sm-0 rounded-lg"
          @click="dialogAddReporte = true"
        >
          <v-icon left small>mdi-file-upload-outline</v-icon>
          Subir archivo
        </v-btn>
        <v-btn
          color="primary"
          small
          depressed
          class="rounded-lg"
          :loading="cargar"
          @click="getReportesBancos()"
        >
          <v-icon left small>mdi-refresh</v-icon>
          Actualizar
        </v-btn>
      </div>
    </div>

    <!-- Barra de filtros global -->
    <v-card class="filters-bar rounded-lg mb-4" flat>
      <v-card-text class="py-3">
        <v-row dense align="center">
          <v-col cols="12" sm="6" md="2">
            <v-select
              v-model="escuelaFilter"
              :items="escuelaOptions"
              label="Escuela"
              hide-details
              dense
              clearable
              outlined
              prepend-inner-icon="mdi-school-outline"
              class="rounded-lg"
            ></v-select>
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <v-select
              v-model="bancoFilter"
              :items="bancoOptions"
              label="Banco"
              hide-details
              dense
              clearable
              outlined
              prepend-inner-icon="mdi-bank"
              class="rounded-lg"
            ></v-select>
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <v-text-field
              label="Desde"
              outlined
              dense
              clearable
              v-model="fechaini"
              hide-details
              type="date"
              prepend-inner-icon="mdi-calendar-start"
              class="rounded-lg"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <v-text-field
              label="Hasta"
              outlined
              dense
              clearable
              v-model="fechafin"
              type="date"
              hide-details
              prepend-inner-icon="mdi-calendar-end"
              class="rounded-lg"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="4" class="d-flex flex-wrap align-center">
            <v-chip v-if="escuelaFilter" small class="mr-2 mb-1" color="primary" outlined close @click:close="escuelaFilter = null">
              {{ escuelaFilter === 1 ? 'INBI' : 'FAST' }}
            </v-chip>
            <v-chip v-if="bancoFilter" small class="mr-2 mb-1" color="primary" outlined close @click:close="bancoFilter = null">
              {{ getBancoLabel(bancoFilter) }}
            </v-chip>
            <span class="text-caption grey--text">{{ rangoFechasLabel }}</span>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Vista: Ingresos vs Egresos -->
    <v-card v-if="vistaActiva === 'ingresosEgresos'" class="ie-card rounded-lg mb-6" flat>
      <div class="ie-card__header pa-4 d-flex align-center">
        <v-icon color="teal" class="mr-2">mdi-table-large</v-icon>
        <div>
          <div class="font-weight-bold">Ingresos vs Egresos</div>
          <div class="text-caption grey--text">
            {{ escuelaFilter ? (escuelaFilter === 1 ? 'INBI' : 'FAST') : 'Todas las escuelas' }}
            · {{ tablaIngresosEgresos.length }} día{{ tablaIngresosEgresos.length !== 1 ? 's' : '' }}
          </div>
        </div>
        <v-spacer></v-spacer>
        <v-btn small text color="primary" @click="vistaActiva = 'listado'">
          <v-icon left small>mdi-arrow-left</v-icon>
          Volver al listado
        </v-btn>
      </div>

      <div class="ie-table-wrap">
        <table class="ie-table">
          <thead>
            <tr class="ie-table__row-meta">
              <th colspan="3" class="ie-th ie-th--sticky ie-th--meta"></th>
              <th colspan="5" class="ie-th ie-th--group ie-th--ingresos">INGRESOS</th>
              <th colspan="5" class="ie-th ie-th--group ie-th--egresos">EGRESOS</th>
              <th colspan="4" class="ie-th ie-th--group ie-th--saldos">SALDOS</th>
            </tr>
            <tr>
              <th class="ie-th ie-th--sticky ie-th--fecha">Escuela</th>
              <th class="ie-th ie-th--sticky ie-th--fecha2">Fecha</th>
              <th class="ie-th ie-th--sticky ie-th--mini">Sem</th>
              <th class="ie-th ie-th--ing">BK</th>
              <th class="ie-th ie-th--ing">BBVA</th>
              <th class="ie-th ie-th--ing">HSBC</th>
              <th class="ie-th ie-th--ing">EFE</th>
              <th class="ie-th ie-th--ing ie-th--total">Total</th>
              <th class="ie-th ie-th--eg">BK</th>
              <th class="ie-th ie-th--eg">BBVA</th>
              <th class="ie-th ie-th--eg">HSBC</th>
              <th class="ie-th ie-th--eg">EFE</th>
              <th class="ie-th ie-th--eg ie-th--total">Total</th>
              <th class="ie-th ie-th--sal">BK</th>
              <th class="ie-th ie-th--sal">BBVA</th>
              <th class="ie-th ie-th--sal">HSBC</th>
              <th class="ie-th ie-th--sal ie-th--total">Diferencia</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in tablaIngresosEgresos" :key="row.key">
              <td class="ie-td ie-td--sticky ie-td--escuela">
                <v-chip x-small :color="row.opEscuela === 1 ? '#E8F4FF' : '#E8EAF6'" :text-color="row.opEscuela === 1 ? '#1976D2' : '#000B58'">
                  {{ row.escuelaLabel }}
                </v-chip>
              </td>
              <td class="ie-td ie-td--sticky ie-td--fecha">{{ row.fechaLarga }}</td>
              <td class="ie-td ie-td--sticky ie-td--mini text-center">{{ row.semana }}</td>
              <td class="ie-td ie-td--money ie-td--ing">{{ formatMontoCelda(row.ingBK) }}</td>
              <td class="ie-td ie-td--money ie-td--ing">{{ formatMontoCelda(row.ingBBVA) }}</td>
              <td class="ie-td ie-td--money ie-td--ing">{{ formatMontoCelda(row.ingHSBC) }}</td>
              <td class="ie-td ie-td--money ie-td--ing">{{ formatMontoCelda(row.ingEFE) }}</td>
              <td class="ie-td ie-td--money ie-td--ing ie-td--total">{{ formatMontoCelda(row.ingTotal) }}</td>
              <td class="ie-td ie-td--money ie-td--eg">{{ formatMontoCelda(row.egBK) }}</td>
              <td class="ie-td ie-td--money ie-td--eg">{{ formatMontoCelda(row.egBBVA) }}</td>
              <td class="ie-td ie-td--money ie-td--eg">{{ formatMontoCelda(row.egHSBC) }}</td>
              <td class="ie-td ie-td--money ie-td--eg">{{ formatMontoCelda(row.egEFE) }}</td>
              <td class="ie-td ie-td--money ie-td--eg ie-td--total">{{ formatMontoCelda(row.egTotal) }}</td>
              <td class="ie-td ie-td--money ie-td--sal">{{ formatMontoCelda(row.saldoBK) }}</td>
              <td class="ie-td ie-td--money ie-td--sal">{{ formatMontoCelda(row.saldoBBVA) }}</td>
              <td class="ie-td ie-td--money ie-td--sal">{{ formatMontoCelda(row.saldoHSBC) }}</td>
              <td class="ie-td ie-td--money ie-td--sal ie-td--total" :class="row.diferencia >= 0 ? 'text-success' : 'text-error'">
                {{ formatMontoCelda(row.diferencia) }}
              </td>
            </tr>
            <tr v-if="!tablaIngresosEgresos.length">
              <td colspan="17" class="ie-td text-center py-8 grey--text">
                <v-icon size="40" color="grey lighten-1">mdi-table-off</v-icon>
                <div class="mt-2">No hay datos para el periodo y filtros seleccionados</div>
              </td>
            </tr>
          </tbody>
          <tfoot v-if="tablaIngresosEgresos.length">
            <tr class="ie-table__footer">
              <td colspan="3" class="ie-td ie-td--sticky font-weight-bold">TOTALES</td>
              <td class="ie-td ie-td--money font-weight-bold">{{ formatMontoCelda(ieTotales.ingBK) }}</td>
              <td class="ie-td ie-td--money font-weight-bold">{{ formatMontoCelda(ieTotales.ingBBVA) }}</td>
              <td class="ie-td ie-td--money font-weight-bold">{{ formatMontoCelda(ieTotales.ingHSBC) }}</td>
              <td class="ie-td ie-td--money font-weight-bold">{{ formatMontoCelda(ieTotales.ingEFE) }}</td>
              <td class="ie-td ie-td--money font-weight-bold">{{ formatMontoCelda(ieTotales.ingTotal) }}</td>
              <td class="ie-td ie-td--money font-weight-bold">{{ formatMontoCelda(ieTotales.egBK) }}</td>
              <td class="ie-td ie-td--money font-weight-bold">{{ formatMontoCelda(ieTotales.egBBVA) }}</td>
              <td class="ie-td ie-td--money font-weight-bold">{{ formatMontoCelda(ieTotales.egHSBC) }}</td>
              <td class="ie-td ie-td--money font-weight-bold">{{ formatMontoCelda(ieTotales.egEFE) }}</td>
              <td class="ie-td ie-td--money font-weight-bold">{{ formatMontoCelda(ieTotales.egTotal) }}</td>
              <td colspan="3"></td>
              <td class="ie-td ie-td--money font-weight-bold" :class="ieTotales.diferencia >= 0 ? 'text-success' : 'text-error'">
                {{ formatMontoCelda(ieTotales.diferencia) }}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </v-card>

    <template v-if="vistaActiva === 'listado'">
    <!-- KPIs principales -->
    <v-row dense class="mb-4">
      <v-col cols="6" sm="6" md="3">
        <div class="kpi-card kpi-card--ingreso">
          <div class="kpi-card__icon-wrap">
            <v-icon size="22" color="success">mdi-arrow-down-circle-outline</v-icon>
          </div>
          <div class="kpi-card__label">Ingresos</div>
          <div class="kpi-card__value text-success">{{ formatMoney(kpiTotales.ingreso) }}</div>
          <div class="kpi-card__meta">{{ kpiTotales.count }} registros</div>
        </div>
      </v-col>
      <v-col cols="6" sm="6" md="3">
        <div class="kpi-card kpi-card--egreso">
          <div class="kpi-card__icon-wrap">
            <v-icon size="22" color="error">mdi-arrow-up-circle-outline</v-icon>
          </div>
          <div class="kpi-card__label">Egresos</div>
          <div class="kpi-card__value text-error">{{ formatMoney(kpiTotales.egreso) }}</div>
          <div class="kpi-card__meta">Periodo filtrado</div>
        </div>
      </v-col>
      <v-col cols="6" sm="6" md="3">
        <div class="kpi-card" :class="kpiTotales.neto >= 0 ? 'kpi-card--neto-pos' : 'kpi-card--neto-neg'">
          <div class="kpi-card__icon-wrap">
            <v-icon size="22" :color="kpiTotales.neto >= 0 ? 'primary' : 'warning'">mdi-scale-balance</v-icon>
          </div>
          <div class="kpi-card__label">Flujo neto</div>
          <div class="kpi-card__value" :class="kpiTotales.neto >= 0 ? 'primary--text' : 'warning--text'">
            {{ formatMoney(kpiTotales.neto) }}
          </div>
          <div class="kpi-card__meta">Ingreso − egreso</div>
        </div>
      </v-col>
      <v-col cols="6" sm="6" md="3">
        <div class="kpi-card kpi-card--archivos">
          <div class="kpi-card__icon-wrap">
            <v-icon size="22" color="grey darken-1">mdi-file-document-multiple-outline</v-icon>
          </div>
          <div class="kpi-card__label">Archivos</div>
          <div class="kpi-card__value">{{ kpiTotales.count }}</div>
          <div class="kpi-card__meta">{{ rangoFechasLabel }}</div>
        </div>
      </v-col>
    </v-row>

    <!-- KPI por escuela -->
    <v-row dense class="mb-4">
      <v-col cols="12" md="6">
        <div class="breakdown-card">
          <div class="breakdown-card__header">
            <v-icon small class="mr-2" color="#44A7FF">mdi-school-outline</v-icon>
            <span>INBI</span>
          </div>
          <div class="breakdown-card__body">
            <div class="breakdown-stat">
              <span class="breakdown-stat__label">Ingreso</span>
              <span class="breakdown-stat__value text-success">{{ formatMoney(kpiPorEscuela.inbi.ingreso) }}</span>
            </div>
            <div class="breakdown-stat">
              <span class="breakdown-stat__label">Egreso</span>
              <span class="breakdown-stat__value text-error">{{ formatMoney(kpiPorEscuela.inbi.egreso) }}</span>
            </div>
            <div class="breakdown-stat">
              <span class="breakdown-stat__label">Neto</span>
              <span class="breakdown-stat__value">{{ formatMoney(kpiPorEscuela.inbi.neto) }}</span>
            </div>
          </div>
        </div>
      </v-col>
      <v-col cols="12" md="6">
        <div class="breakdown-card">
          <div class="breakdown-card__header">
            <v-icon small class="mr-2" color="#000B58">mdi-lightning-bolt-outline</v-icon>
            <span>FAST</span>
          </div>
          <div class="breakdown-card__body">
            <div class="breakdown-stat">
              <span class="breakdown-stat__label">Ingreso</span>
              <span class="breakdown-stat__value text-success">{{ formatMoney(kpiPorEscuela.fast.ingreso) }}</span>
            </div>
            <div class="breakdown-stat">
              <span class="breakdown-stat__label">Egreso</span>
              <span class="breakdown-stat__value text-error">{{ formatMoney(kpiPorEscuela.fast.egreso) }}</span>
            </div>
            <div class="breakdown-stat">
              <span class="breakdown-stat__label">Neto</span>
              <span class="breakdown-stat__value">{{ formatMoney(kpiPorEscuela.fast.neto) }}</span>
            </div>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- KPI por banco -->
    <v-row dense class="mb-6">
      <v-col
        v-for="banco in kpiPorBanco"
        :key="banco.key"
        cols="12"
        sm="4"
      >
        <div class="bank-kpi" @click="bancoFilter = bancoFilter === banco.key ? null : banco.key">
          <div class="bank-kpi__avatar" :style="{ background: banco.color }">
            <span class="bank-kpi__abbr">{{ banco.abbr }}</span>
          </div>
          <div class="bank-kpi__info">
            <div class="bank-kpi__name">{{ banco.label }}</div>
            <div class="bank-kpi__amounts">
              <span class="text-success">{{ formatMoney(banco.ingreso) }}</span>
              <span class="bank-kpi__sep">/</span>
              <span class="text-error">{{ formatMoney(banco.egreso) }}</span>
            </div>
            <div class="bank-kpi__meta">{{ banco.count }} archivo{{ banco.count !== 1 ? 's' : '' }}</div>
          </div>
          <v-icon
            v-if="bancoFilter === banco.key"
            small
            color="primary"
            class="bank-kpi__active"
          >mdi-check-circle</v-icon>
        </div>
      </v-col>
    </v-row>

    <!-- Tabla detalle -->
    <v-card class="table-card rounded-lg" flat>
      <div class="table-card__toolbar pa-4">
        <v-row dense align="center">
          <v-col cols="12" md="8">
            <v-text-field
              v-model="search"
              prepend-inner-icon="mdi-magnify"
              label="Buscar en tabla..."
              single-line
              hide-details
              dense
              outlined
              clearable
              class="rounded-lg"
            ></v-text-field>
          </v-col>
        </v-row>
      </div>

      <v-data-table
        :headers="headersReporte"
        :items="filteredResults"
        :search="search"
        :items-per-page="10"
        :footer-props="{ 'items-per-page-options': [10, 25, 50] }"
        class="estado-table elevation-0"
        mobile-breakpoint="600"
      >
        <template v-slot:item.fecha="{ item }">
          <div class="d-flex align-center fecha-cell">
            <v-icon x-small class="mr-1 grey--text">mdi-calendar-blank-outline</v-icon>
            <span>{{ item.fecha }}</span>
          </div>
        </template>

        <template v-slot:item.bancoArchivo="{ item }">
          <div class="d-flex align-center">
            <div
              class="bank-avatar mr-2"
              :style="{ background: getBankMeta(item.bancoArchivo).color }"
            >
              {{ getBankMeta(item.bancoArchivo).abbr }}
            </div>
            <span class="font-weight-medium">{{ getBankMeta(item.bancoArchivo).label }}</span>
          </div>
        </template>

        <template v-slot:item.opEscuela="{ item }">
          <v-chip
            small
            :color="item.opEscuela == 1 ? '#E8F4FF' : '#E8EAF6'"
            :text-color="item.opEscuela == 1 ? '#1976D2' : '#000B58'"
            class="escuela-chip"
          >
            <v-icon left x-small>{{ item.opEscuela == 1 ? 'mdi-school' : 'mdi-lightning-bolt' }}</v-icon>
            {{ item.opEscuela == 1 ? 'INBI' : 'FAST' }}
          </v-chip>
        </template>

        <template v-slot:item.tipoArchivo="{ item }">
          <v-chip
            small
            outlined
            :color="getTipoArchivo(item.tipoArchivo)"
            class="tipo-chip"
          >
            <v-icon left x-small>{{ getTipoArchivoIcon(item.tipoArchivo) }}</v-icon>
            {{ item.tipoArchivo }}
          </v-chip>
        </template>

        <template v-slot:item.ingreso="{ item }">
          <span class="money-cell text-success font-weight-medium">
            {{ formatMoney(item.ingreso) }}
          </span>
        </template>

        <template v-slot:item.egreso="{ item }">
          <span class="money-cell text-error font-weight-medium">
            {{ formatMoney(item.egreso) }}
          </span>
        </template>

        <template v-slot:no-data>
          <div class="py-8 text-center grey--text">
            <v-icon size="48" color="grey lighten-1">mdi-file-search-outline</v-icon>
            <p class="mt-3 mb-0">No hay registros para el periodo seleccionado</p>
          </div>
        </template>
      </v-data-table>
    </v-card>
    </template>

    <!-- Dialogo para cargar archivos -->
    <v-dialog v-model="dialogAddReporte" max-width="540" scrollable>
      <v-card>

        <!-- Titulo para mi dialogo -->
        <v-card-title class="text-subtitle-1">
          Subir un archivo
          <v-spacer></v-spacer>

          <!-- Boton para cerrar mi cuadro de dialogo -->
          <v-btn icon @click="dialogAddReporte = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <!-- Cuerpo de mi cuadro de dialogo -->
        <v-card-text class="overflow-y-auto" style="max-height: 70vh;">

          <!-- Opciones para agregar el archivo -->
        	<v-row>

            <!-- Opción de banco -->
        		<v-col cols="6">
        			<label>Selecciona un banco</label>
        			<v-select
                v-model="bancoArchivo"
                :items="bancosArchivos"
                label="Banco"
                hide-details
                dense
                single-line
                filled
              ></v-select>
        		</v-col>

            <!-- Opción para el tipo de archivo -->
        		<v-col cols="6">
        			<label>Selecciona tipo de archivo</label>
        			<v-select
                v-model="tipoArchivo"
                :items="tiposArchivos"
                label="Tipo de archivo"
                hide-details
                dense
                single-line
                filled
              ></v-select>
        		</v-col>

            <!-- Opción para seleccionar cuenta de banco -->
            <v-col cols="12">
              <label>Selecciona una cuenta</label>
              <v-autocomplete
                v-model="idcuentas_escuela"
                :items="cuentasParaAutocomplete"
                :loading="cargandoCuentas"
                item-text="cuenta_completa"
                item-value="idcuentas_escuela"
                label="Cuenta de banco"
                hide-details
                dense
                filled
                clearable
                no-data-text="No hay cuentas disponibles"
                placeholder="Buscar cuenta..."
              ></v-autocomplete>
            </v-col>

            <!-- Opción para el tipo de archivo -->
            <v-col cols="12">
              <label>Selecciona una escuela</label>
              <v-radio-group
                v-model="opEscuela"
                hide-details
                row
              >
                <v-radio
                  label="INBI"
                  :value="1"
                ></v-radio>
                <v-radio
                  label="FAST"
                  :value="2"
                ></v-radio>
              </v-radio-group>
            </v-col>
             
		         
		        <!-- Subir el archivo en tipo excel -->
        		<v-col cols="12">
		          <v-file-input
		            v-model="excelIngresos"
		            label="Archivo de tipo Excel"
		            hide-details
                single-line
		            filled
		            dense
		          ></v-file-input>
        		</v-col>

            <!-- Vista previa: mismas reglas de lectura que al guardar -->
            <v-col cols="12" v-if="excelIngresos && bancoArchivo && tipoArchivo">
              <v-progress-linear
                v-if="cargandoPreview"
                indeterminate
                color="green"
              ></v-progress-linear>
              <div v-if="cargandoPreview" class="caption grey--text mt-1">Calculando registros del archivo…</div>

              <v-sheet v-else-if="previewExcel" outlined rounded class="pa-3">
                <div class="text-subtitle-2 mb-2">Vista previa del archivo</div>
                <div><b>Registros a subir:</b> {{ previewExcel.registros }}</div>
                <div v-if="previewExcel.fecha_inicio">
                  <b>Fechas:</b> del {{ previewExcel.fecha_inicio }} al {{ previewExcel.fecha_fin }}
                </div>
                <div><b>Total abonos:</b> {{ formatMoney(previewExcel.total_abonos) }}</div>
                <div><b>Total {{ etiquetaCargosPreview }}:</b> {{ formatMoney(previewExcel.total_cargos) }}</div>
                <div v-if="previewExcel.por_hoja && previewExcel.por_hoja.length" class="caption mt-1">
                  <b>Por hoja:</b>
                  <span v-for="(h, i) in previewExcel.por_hoja" :key="h.hoja">
                    {{ h.hoja }}: {{ h.registros }}<span v-if="i < previewExcel.por_hoja.length - 1"> · </span>
                  </span>
                </div>
              </v-sheet>

              <v-alert
                v-if="motivoBloqueoGuardar && !cargandoPreview"
                type="warning"
                dense
                outlined
                class="mt-2 mb-0"
              >
                {{ motivoBloqueoGuardar }}
              </v-alert>
            </v-col>

        	</v-row>
        </v-card-text>

        <!-- Sección del dialogo para enviar el archivo a guardar -->
        <v-card-actions>
	        <v-btn 
            block 
            color="green" 
            :dark="!motivoBloqueoGuardar"
            :disabled="!!motivoBloqueoGuardar"
            @click="saveExcel( )"
          >
            Guardar
            <v-icon left>mdi-content-save</v-icon>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Confirmación antes de subir el archivo -->
    <v-dialog v-model="dialogConfirmarSubida" max-width="440" persistent>
      <v-card>
        <v-card-title class="text-subtitle-1">Confirmar subida</v-card-title>
        <v-card-text>
          Se van a subir <b>{{ previewExcel ? previewExcel.registros : 0 }}</b> registros<span v-if="previewExcel && previewExcel.fecha_inicio"> (del {{ previewExcel.fecha_inicio }} al {{ previewExcel.fecha_fin }})</span>. ¿Continuar?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="dialogConfirmarSubida = false">Cancelar</v-btn>
          <v-btn color="green" dark @click="subirExcel( )">Subir</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialogo para descargar los archivos subidos -->
    <v-dialog v-model="dialogArchivosSubidos" max-width="1000" scrollable>
      <v-card class="rounded-lg overflow-hidden">
        <v-card-title class="primary white--text py-4">
          <div>
            <div class="text-overline white--text text--lighten-2">Estado de cuenta bancos</div>
            <div class="text-h6 font-weight-bold">Archivos subidos</div>
          </div>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="dialogArchivosSubidos = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-4">
          <v-row dense align="center" class="mb-2">
            <v-col cols="12" sm="5" md="4">
              <v-text-field
                label="Subidos desde"
                outlined
                dense
                v-model="fechainiArchivos"
                hide-details
                type="date"
                prepend-inner-icon="mdi-calendar-start"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="5" md="4">
              <v-text-field
                label="Subidos hasta"
                outlined
                dense
                v-model="fechafinArchivos"
                type="date"
                hide-details
                prepend-inner-icon="mdi-calendar-end"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="2" md="4" class="d-flex justify-sm-end">
              <v-btn color="primary" depressed :loading="cargandoArchivosSubidos" @click="getArchivosSubidos()">
                <v-icon left small>mdi-refresh</v-icon>
                Actualizar
              </v-btn>
            </v-col>
          </v-row>

          <div class="caption grey--text mb-3">
            Se aplican los filtros de Escuela y Banco de la vista.
            Los archivos subidos antes de que se guardaran los Excel aparecen como "No disponible".
          </div>

          <v-data-table
            dense
            :headers="headersArchivosSubidos"
            :items="archivosSubidosFiltrados"
            :loading="cargandoArchivosSubidos"
            :items-per-page="10"
            sort-by="fecha_creacion"
            sort-desc
            no-data-text="No hay archivos subidos en este rango"
            class="elevation-0"
          >
            <template v-slot:item.bancoArchivo="{ item }">
              <v-chip small dark :color="getBancoColor(item.bancoArchivo)">{{ getBancoLabel(item.bancoArchivo) }}</v-chip>
            </template>
            <template v-slot:item.tipoArchivo="{ item }">
              <v-chip small dark :color="getTipoArchivo(item.tipoArchivo)">{{ item.tipoArchivo }}</v-chip>
            </template>
            <template v-slot:item.opEscuela="{ item }">
              {{ Number(item.opEscuela) === 1 ? 'INBI' : (Number(item.opEscuela) === 2 ? 'FAST' : '—') }}
            </template>
            <template v-slot:item.acciones="{ item }">
              <v-btn
                v-if="item.archivo_disponible"
                small
                depressed
                color="indigo"
                dark
                :loading="descargandoArchivoId === item.idexcel_ingresos"
                @click="descargarArchivoSubido(item)"
              >
                <v-icon left small>mdi-download</v-icon>
                Descargar
              </v-btn>
              <span v-else class="caption grey--text">No disponible</span>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Dialogo para ver archivos faltantes -->
    <v-dialog v-model="reporteArchivosFaltantes" max-width="960" scrollable>
      <v-card class="faltantes-dialog rounded-lg overflow-hidden">
        <v-card-title class="faltantes-dialog__header primary white--text py-4">
          <div>
            <div class="text-overline white--text text--lighten-2">Estado de cuenta bancos</div>
            <div class="text-h6 font-weight-bold">Archivos faltantes</div>
          </div>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="reporteArchivosFaltantes = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-4">
          <v-alert
            type="info"
            dense
            outlined
            class="mb-4 faltantes-leyenda"
            icon="mdi-information-outline"
          >
            <div class="d-flex flex-wrap align-center">
              <span class="mr-4 mb-1">
                <strong>NA (No aplica):</strong> sábado y domingo en Banregio y HSBC — el banco reporta al siguiente día hábil.
              </span>
              <v-chip small label color="green darken-1" dark class="mr-2 mb-1">SI</v-chip>
              <v-chip small label color="red darken-1" dark class="mr-2 mb-1">NO</v-chip>
              <v-chip small label outlined color="blue-grey lighten-4" class="faltantes-chip faltantes-chip--na mb-1">
                <v-icon left x-small>mdi-calendar-remove-outline</v-icon>
                NA
              </v-chip>
            </div>
          </v-alert>

          <v-row dense align="center" class="mb-4">
            <v-col cols="12" sm="5" md="4">
              <v-text-field
                label="Fecha de inicio"
                outlined
                dense
                clearable
                v-model="fechainiA"
                hide-details
                type="date"
                prepend-inner-icon="mdi-calendar-start"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="5" md="4">
              <v-text-field
                label="Fecha final"
                outlined
                dense
                clearable
                v-model="fechafinA"
                type="date"
                hide-details
                prepend-inner-icon="mdi-calendar-end"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="2" md="4" class="d-flex justify-sm-end">
              <v-btn
                color="primary"
                depressed
                :loading="cargar"
                @click="getArchivosFaltantes()"
              >
                <v-icon left small>mdi-refresh</v-icon>
                Actualizar
              </v-btn>
            </v-col>
          </v-row>

          <div v-if="archivosFaltantes.length" class="d-flex flex-wrap mb-3 faltantes-resumen">
            <v-chip small class="mr-2 mb-1" color="grey lighten-3">
              {{ archivosFaltantes.length }} días
            </v-chip>
            <v-chip small class="mr-2 mb-1" color="green darken-1" dark>
              {{ resumenArchivosFaltantes.si }} con archivo
            </v-chip>
            <v-chip small class="mr-2 mb-1" color="red darken-1" dark>
              {{ resumenArchivosFaltantes.no }} faltantes
            </v-chip>
            <v-chip small class="mr-2 mb-1" color="blue-grey lighten-4">
              {{ resumenArchivosFaltantes.na }} NA (fin de semana)
            </v-chip>
          </div>

          <v-data-table
            :headers="headersArchivosFaltantes"
            :items="archivosFaltantes"
            class="faltantes-table elevation-0"
            dense
            :loading="cargar"
            loading-text="Consultando archivos..."
            :items-per-page="15"
            :item-class="claseFilaArchivoFaltante"
            :footer-props="{ 'items-per-page-options': [10, 15, 25, 50] }"
          >
            <template v-slot:item.fecha="{ item }">
              <div class="faltantes-fecha">
                <span class="font-weight-medium">{{ item.fecha }}</span>
                <span
                  class="faltantes-fecha__dia"
                  :class="{ 'faltantes-fecha__dia--fin': esFinDeSemana(item.fecha) }"
                >
                  {{ nombreDiaSemana(item.fecha) }}
                </span>
              </div>
            </template>

            <template v-slot:item.BANREGIO_EDO_CUENTA="{ item }">
              <v-chip
                v-bind="chipEstadoArchivo(item, 'BANREGIO_EDO_CUENTA')"
                small
                label
                class="faltantes-chip"
                :class="{ 'faltantes-chip--na': estadoArchivoCelda(item, 'BANREGIO_EDO_CUENTA').esNA }"
              >
                <v-icon
                  v-if="estadoArchivoCelda(item, 'BANREGIO_EDO_CUENTA').icon"
                  left
                  x-small
                >
                  {{ estadoArchivoCelda(item, 'BANREGIO_EDO_CUENTA').icon }}
                </v-icon>
                {{ estadoArchivoCelda(item, 'BANREGIO_EDO_CUENTA').texto }}
              </v-chip>
            </template>

            <template v-slot:item.BBVA_EDO_CUENTA="{ item }">
              <v-chip
                v-bind="chipEstadoArchivo(item, 'BBVA_EDO_CUENTA')"
                small
                label
                class="faltantes-chip"
                :class="{ 'faltantes-chip--na': estadoArchivoCelda(item, 'BBVA_EDO_CUENTA').esNA }"
              >
                <v-icon
                  v-if="estadoArchivoCelda(item, 'BBVA_EDO_CUENTA').icon"
                  left
                  x-small
                >
                  {{ estadoArchivoCelda(item, 'BBVA_EDO_CUENTA').icon }}
                </v-icon>
                {{ estadoArchivoCelda(item, 'BBVA_EDO_CUENTA').texto }}
              </v-chip>
            </template>

            <template v-slot:item.HSBC_EDO_CUENTA="{ item }">
              <v-chip
                v-bind="chipEstadoArchivo(item, 'HSBC_EDO_CUENTA')"
                small
                label
                class="faltantes-chip"
                :class="{ 'faltantes-chip--na': estadoArchivoCelda(item, 'HSBC_EDO_CUENTA').esNA }"
              >
                <v-icon
                  v-if="estadoArchivoCelda(item, 'HSBC_EDO_CUENTA').icon"
                  left
                  x-small
                >
                  {{ estadoArchivoCelda(item, 'HSBC_EDO_CUENTA').icon }}
                </v-icon>
                {{ estadoArchivoCelda(item, 'HSBC_EDO_CUENTA').texto }}
              </v-chip>
            </template>

            <template v-slot:item.HSBC_TPV="{ item }">
              <v-chip
                v-bind="chipEstadoArchivo(item, 'HSBC_TPV')"
                small
                label
                class="faltantes-chip"
                :class="{ 'faltantes-chip--na': estadoArchivoCelda(item, 'HSBC_TPV').esNA }"
              >
                <v-icon
                  v-if="estadoArchivoCelda(item, 'HSBC_TPV').icon"
                  left
                  x-small
                >
                  {{ estadoArchivoCelda(item, 'HSBC_TPV').icon }}
                </v-icon>
                {{ estadoArchivoCelda(item, 'HSBC_TPV').texto }}
              </v-chip>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-dialog>


    <!-- Snackbar para notificaciones -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="snackbar.timeout">
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">
          Cerrar
        </v-btn>
      </template>
    </v-snackbar>

    <!-- Dialog de carga -->
    <Alerta v-if="respuestaAlerta" :parametros="parametros" />
    <carga v-if="cargar" />

  </v-container>
</template>

<script>
import axios from 'axios';
import * as XLSX from 'xlsx';

// Componentes
import Alerta         from "@/components/alertas/Alerta.vue";
import carga          from "@/components/alertas/carga.vue";
import validarErrores from "@/mixins/validarErrores";
import { mapGetters } from 'vuex'

export default {
  name: 'ValidationPanel',
  data() {
    return {

      // Alertas
      parametros: {
        dialogError: false,
        mensaje: "",
        color: "",
      },

      respuestaAlerta: false,
      loader: true,
      cargar: false,

      dialogAddReporte: false,
      reporteArchivosFaltantes: false,

      tipoArchivo:null,
			tiposArchivos:['EDO CUENTA', 'TPV', 'COMERCIO ELECTRONICO'],

      opEscuela: null,

			bancoArchivo: null,
			bancosArchivos: ['BBVA', 'BANREGIO', 'HSBC'],

      excelIngresos: null,
      previewExcel: null,
      cargandoPreview: false,
      errorPreview: '',
      previewSeq: 0,
      dialogConfirmarSubida: false,

      idcuentas_escuela: null,
      cuentas: [],
      cargandoCuentas: false,

      fechaini: null,// (new Date(Date.now() - (new Date()).getTimezoneOffset() * 60000)).toISOString().substr(0, 10),
      fechafin: null,// (new Date(Date.now() - (new Date()).getTimezoneOffset() * 60000)).toISOString().substr(0, 10),

      fechainiA:null,// (new Date(Date.now() - (new Date()).getTimezoneOffset() * 60000)).toISOString().substr(0, 10),
      fechafinA:null,// (new Date(Date.now() - (new Date()).getTimezoneOffset() * 60000)).toISOString().substr(0, 10),

      bankMeta: {
        BBVA: { label: 'BBVA', color: '#004481', abbr: 'BB' },
        BANREGIO: { label: 'BanRegio', color: '#6B2D8C', abbr: 'BR' },
        HSBC: { label: 'HSBC', color: '#DB0011', abbr: 'HS' },
      },

      // Header estado de cuenta
      headersReporte: [
        { text: 'Fecha', value: 'fecha' },
        { text: 'Banco', value: 'bancoArchivo' },
        { text: 'Escuela', value: 'opEscuela' },
        { text: 'Tipo', value: 'tipoArchivo' },
        { text: 'Ingreso', value: 'ingreso', align: 'end' },
        { text: 'Egreso', value: 'egreso', align: 'end' },
      ],

      headersArchivosFaltantes: [
        { text: 'Fecha', value: 'fecha', width: '140px' },
        { text: 'Banregio Edo. Cuenta', value: 'BANREGIO_EDO_CUENTA', align: 'center', sortable: false },
        { text: 'BBVA Edo. Cuenta', value: 'BBVA_EDO_CUENTA', align: 'center', sortable: false },
        { text: 'HSBC Edo. Cuenta', value: 'HSBC_EDO_CUENTA', align: 'center', sortable: false },
        { text: 'HSBC TPV', value: 'HSBC_TPV', align: 'center', sortable: false },
      ],

      columnasFinSemanaNA: ['BANREGIO_EDO_CUENTA', 'HSBC_EDO_CUENTA', 'HSBC_TPV'],

      archivosFaltantes: [],


      vistaActiva: 'listado',

      // Filtros
      bancoFilter: null,
      escuelaFilter: null,

      dialogArchivosSubidos: false,
      archivosSubidos: [],
      cargandoArchivosSubidos: false,
      descargandoArchivoId: null,
      fechainiArchivos: null,
      fechafinArchivos: null,
      headersArchivosSubidos: [
        { text: 'Subido'    , value: 'fecha_creacion'  },
        { text: 'Archivo'   , value: 'excelname'       },
        { text: 'Banco'     , value: 'bancoArchivo'    },
        { text: 'Tipo'      , value: 'tipoArchivo'     },
        { text: 'Escuela'   , value: 'opEscuela'       },
        { text: 'Registros' , value: 'total_registros' },
        { text: ''          , value: 'acciones', sortable: false },
      ],

      bancoOptions: ['BBVA', 'BANREGIO', 'HSBC'],
      escuelaOptions: [
        { text: 'INBI', value: 1 },
        { text: 'FAST', value: 2 },
      ],

      excelFile: null,
      loading: false,
      dbData: [],
      processedResults: [],
      search: '',

      snackbar: {
        show: false,
        text: '',
        color: '',
        timeout: 4000
      },

      ingresosSeleccionados:[],

      // Búsqueda desde detalles
      detailSearch: {
        referencia: '',
      },

      archivosLeidos:[],
      

      // LECTURA DE ARCHIVO
      opNomina: 1,
      dialogPDFDetalles: false,

      partidasNomina:[],
      archivoSeleccionado: null,
      dialgoValidaMultiple: false,
      dataMultiple: [],
      RX_ONLY_LEADING_DIGITS: /^\s*(\d+)/,
      RX_SPEI_NOMBRE: /^\s*\d{3}\s+\d+\s+(.+?)\s*$/,
      GENERICOS: new Set(['sin informacion', 'sin información', 'transferencia', 'pago', 'deposito', 'depósito', 'abono', 'sin datos', 'n/a', 'pendiente', 'traspaso', 'traspaso interno'])
    }
  },

  components: {
    Alerta,
    carga,
  },

  watch:{

    archivoSeleccionado ( value ){

      if( value ){

        this.archivosLeidosDetalle = value.detalles
      
        this.processExcel( )
      
      }
    },

    excelIngresos(){ this.actualizarPreviewExcel() },
    bancoArchivo(){ this.actualizarPreviewExcel() },
    tipoArchivo(){ this.actualizarPreviewExcel() },

  },


  created( ) {
    const hoy = (new Date(Date.now() - (new Date()).getTimezoneOffset() * 60000)).toISOString().substr(0, 10)
    const partes = hoy.split('-')
    this.fechaini = partes[0] + '-' + partes[1] + '-01'
    this.fechafin = hoy
    this.fechainiA = this.fechaini
    this.fechafinA = hoy

    this.getReportesBancos( )
    this.getCuentas( )
  
  },

  mixins: [validarErrores],

  computed: {

    ...mapGetters('login', ['getdatosUsuarioERPERP']),

    archivosSubidosFiltrados() {
      let lista = this.archivosSubidos || []
      if (this.bancoFilter) {
        lista = lista.filter((el) => el.bancoArchivo == this.bancoFilter)
      }
      if (this.escuelaFilter) {
        lista = lista.filter((el) => Number(el.opEscuela) === Number(this.escuelaFilter))
      }
      return lista
    },

    etiquetaCargosPreview() {
      return this.bancoArchivo === 'HSBC' && this.tipoArchivo === 'TPV' ? 'comisiones' : 'cargos'
    },

    motivoBloqueoGuardar() {
      if (!this.excelIngresos || !this.bancoArchivo || !this.tipoArchivo) return ''
      if (this.cargandoPreview) return 'Calculando registros del archivo…'
      if (this.errorPreview) return this.errorPreview
      if (!this.previewExcel) return 'No se pudo obtener la vista previa del archivo.'
      if (this.previewExcel.ya_subido) {
        return `Este archivo ya está cargado (${this.previewExcel.archivo_existente || 'mismo archivo'}). No se puede subir otra vez.`
      }
      if (!this.previewExcel.registros) {
        return 'El archivo no contiene registros válidos para el banco y tipo de archivo seleccionados.'
      }
      return ''
    },

    cuentasParaAutocomplete() {
      return (this.cuentas || []).map(c => ({
        ...c,
        cuenta_completa: c.cuenta_completa || [c.cuenta, c.banco].filter(Boolean).join(' - ') || `Cuenta ${c.idcuentas_escuela}`
      }))
    },

    datosFiltrados() {
      let results = this.archivosLeidos

      if (this.bancoFilter) {
        results = results.filter(item => item.bancoArchivo === this.bancoFilter)
      }

      if (this.escuelaFilter) {
        results = results.filter(item => Number(item.opEscuela) === Number(this.escuelaFilter))
      }

      return results
    },

    filteredResults() {
      return this.datosFiltrados
    },

    tablaIngresosEgresos() {
      return this.buildTablaIngresosEgresos(this.datosFiltrados)
    },

    ieTotales() {
      const rows = this.tablaIngresosEgresos
      const sum = (key) => rows.reduce((acc, row) => acc + (row[key] || 0), 0)
      const ingTotal = sum('ingTotal')
      const egTotal = sum('egTotal')
      return {
        ingBK: sum('ingBK'),
        ingBBVA: sum('ingBBVA'),
        ingHSBC: sum('ingHSBC'),
        ingEFE: sum('ingEFE'),
        ingTotal,
        egBK: sum('egBK'),
        egBBVA: sum('egBBVA'),
        egHSBC: sum('egHSBC'),
        egEFE: sum('egEFE'),
        egTotal,
        diferencia: ingTotal - egTotal,
      }
    },

    resumenArchivosFaltantes () {
      const campos = ['BANREGIO_EDO_CUENTA', 'BBVA_EDO_CUENTA', 'HSBC_EDO_CUENTA', 'HSBC_TPV']
      let si = 0
      let no = 0
      let na = 0

      for ( const item of this.archivosFaltantes || [] ) {
        for ( const campo of campos ) {
          const estado = this.estadoArchivoCelda( item, campo )
          if ( estado.texto === 'SI' ) si++
          else if ( estado.texto === 'NA' ) na++
          else no++
        }
      }

      return { si, no, na }
    },

    rangoFechasLabel() {
      if (this.fechaini && this.fechafin) {
        return `${this.fechaini} → ${this.fechafin}`
      }
      return 'Sin rango'
    },

    kpiTotales() {
      return this.calcularTotales(this.filteredResults)
    },

    kpiPorEscuela() {
      const inbi = this.calcularTotales(
        this.filteredResults.filter(item => Number(item.opEscuela) === 1)
      )
      const fast = this.calcularTotales(
        this.filteredResults.filter(item => Number(item.opEscuela) === 2)
      )
      return { inbi, fast }
    },

    kpiPorBanco() {
      return this.bancoOptions.map((key) => {
        const items = this.datosFiltrados.filter(item => item.bancoArchivo === key)
        const totales = this.calcularTotales(items)
        const meta = this.getBankMeta(key)
        return {
          key,
          label: meta.label,
          abbr: meta.abbr,
          color: meta.color,
          ingreso: totales.ingreso,
          egreso: totales.egreso,
          neto: totales.neto,
          count: totales.count,
        }
      })
    },
  },

  methods: {

    parseMonto(val) {
      if (val == null || val === '') return 0
      if (typeof val === 'number') return Number.isFinite(val) ? val : 0

      const cleaned = String(val)
        .replace(/\$/g, '')
        .replace(/MXN/gi, '')
        .replace(/\s/g, '')
        .replace(/,/g, '')

      const n = parseFloat(cleaned)
      return Number.isFinite(n) ? n : 0
    },

    calcularTotales(items) {
      const lista = Array.isArray(items) ? items : []
      let ingreso = 0
      let egreso = 0
      lista.forEach((item) => {
        ingreso += this.parseMonto(item.ingreso)
        egreso += this.parseMonto(item.egreso)
      })
      return {
        ingreso,
        egreso,
        neto: ingreso - egreso,
        count: lista.length,
      }
    },

    formatMoney(val) {
      const n = this.parseMonto(val)
      return n.toLocaleString('es-MX', {
        style: 'currency',
        currency: 'MXN',
        minimumFractionDigits: 2,
      })
    },

    formatMontoCelda(val) {
      const n = typeof val === 'number' ? val : this.parseMonto(val)
      if (!n) return '-'
      return this.formatMoney(n)
    },

    toggleVistaIngresosEgresos() {
      this.vistaActiva = this.vistaActiva === 'ingresosEgresos' ? 'listado' : 'ingresosEgresos'
    },

    fmtFechaKey(fecha) {
      if (!fecha) return ''
      return String(fecha).slice(0, 10)
    },

    formatFechaLarga(fechaKey) {
      if (!fechaKey) return '-'
      const d = new Date(`${fechaKey}T12:00:00`)
      if (Number.isNaN(d.getTime())) return fechaKey
      return d.toLocaleDateString('es-MX', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    },

    getSemanaDelAnio(fechaKey) {
      if (!fechaKey) return '-'
      const d = new Date(`${fechaKey}T12:00:00`)
      if (Number.isNaN(d.getTime())) return '-'
      const start = new Date(d.getFullYear(), 0, 1)
      const dayOfYear = Math.floor((d - start) / 86400000) + 1
      return Math.ceil(dayOfYear / 7)
    },

    bancoPivotKey(bancoArchivo) {
      const key = this.normalizeBancoKey(bancoArchivo)
      if (key === 'BANREGIO') return 'BK'
      if (key === 'BBVA') return 'BBVA'
      if (key === 'HSBC') return 'HSBC'
      return null
    },

    crearFilaIE(fechaKey, opEscuela) {
      return {
        key: `${fechaKey}|${opEscuela}`,
        fechaKey,
        fechaLarga: this.formatFechaLarga(fechaKey),
        semana: this.getSemanaDelAnio(fechaKey),
        opEscuela,
        escuelaLabel: Number(opEscuela) === 1 ? 'INBI' : 'FAST',
        ingBK: 0, ingBBVA: 0, ingHSBC: 0, ingEFE: 0,
        egBK: 0, egBBVA: 0, egHSBC: 0, egEFE: 0,
        ingTotal: 0, egTotal: 0, diferencia: 0,
        saldoBK: 0, saldoBBVA: 0, saldoHSBC: 0, saldoTotal: 0,
      }
    },

    buildTablaIngresosEgresos(items) {
      const lista = Array.isArray(items) ? items : []
      const map = {}

      lista.forEach((item) => {
        const fechaKey = this.fmtFechaKey(item.fecha)
        const opEscuela = Number(item.opEscuela) || 2
        const rowKey = `${fechaKey}|${opEscuela}`
        if (!map[rowKey]) map[rowKey] = this.crearFilaIE(fechaKey, opEscuela)

        const row = map[rowKey]
        const ing = this.parseMonto(item.ingreso)
        const eg = this.parseMonto(item.egreso)
        const tipo = String(item.tipoArchivo || '').toUpperCase()
        const esEfe = tipo.includes('COMERCIO') || tipo.includes('ELECTRONICO')

        if (esEfe) {
          row.ingEFE += ing
          row.egEFE += eg
        } else {
          const banco = this.bancoPivotKey(item.bancoArchivo)
          if (banco === 'BK') { row.ingBK += ing; row.egBK += eg }
          else if (banco === 'BBVA') { row.ingBBVA += ing; row.egBBVA += eg }
          else if (banco === 'HSBC') { row.ingHSBC += ing; row.egHSBC += eg }
        }
      })

      let rows = Object.values(map).map((row) => {
        row.ingTotal = row.ingBK + row.ingBBVA + row.ingHSBC + row.ingEFE
        row.egTotal = row.egBK + row.egBBVA + row.egHSBC + row.egEFE
        row.diferencia = row.ingTotal - row.egTotal
        return row
      }).sort((a, b) => {
        const cmp = a.fechaKey.localeCompare(b.fechaKey)
        return cmp !== 0 ? cmp : a.opEscuela - b.opEscuela
      })

      const saldosPorEscuela = {}
      rows.forEach((row) => {
        const sk = row.opEscuela
        if (!saldosPorEscuela[sk]) {
          saldosPorEscuela[sk] = { BK: 0, BBVA: 0, HSBC: 0 }
        }
        saldosPorEscuela[sk].BK += row.ingBK - row.egBK
        saldosPorEscuela[sk].BBVA += row.ingBBVA - row.egBBVA
        saldosPorEscuela[sk].HSBC += row.ingHSBC - row.egHSBC
        row.saldoBK = saldosPorEscuela[sk].BK
        row.saldoBBVA = saldosPorEscuela[sk].BBVA
        row.saldoHSBC = saldosPorEscuela[sk].HSBC
        row.saldoTotal = row.saldoBK + row.saldoBBVA + row.saldoHSBC
      })

      return rows
    },

    getBankMeta(banco) {
      const key = this.normalizeBancoKey(banco)
      if (key && this.bankMeta[key]) {
        return { key, ...this.bankMeta[key] }
      }
      const label = this.getBancoLabel(banco)
      return {
        key: label,
        label,
        color: '#607D8B',
        abbr: (label || 'BK').substring(0, 2).toUpperCase(),
      }
    },

    normalizeBancoKey(banco) {
      if (!banco) return null
      const upper = banco.toString().toUpperCase()
      if (upper.includes('BBVA') || upper.includes('BANCOMER')) return 'BBVA'
      if (upper.includes('BANREGIO') || upper === 'BR') return 'BANREGIO'
      if (upper.includes('HSBC')) return 'HSBC'
      return upper
    },

    getTipoArchivoIcon(tipo) {
      if (!tipo) return 'mdi-file-outline'
      const upper = tipo.toString().toUpperCase()
      if (upper.includes('EDO CUENTA')) return 'mdi-file-chart-outline'
      if (upper.includes('TPV')) return 'mdi-credit-card-outline'
      if (upper.includes('COMERCIO')) return 'mdi-cart-outline'
      return 'mdi-file-outline'
    },

    showSnackbar(text, color = 'info') {
      this.snackbar.text = text;
      this.snackbar.color = color;
      this.snackbar.show = true;
    },

    exportResults() {
      if (!this.processedResults.length) {
        this.showSnackbar('No hay resultados para exportar', 'warning');
        return;
      }

      try {
        // Crear una hoja de trabajo
        const worksheet = XLSX.utils.json_to_sheet(this.processedResults.map(item => ({
          Fecha: item.fecha,
          Concepto: item.concepto,
          Monto: item.monto,
          Banco: item.banco,
          'Referencia Excel': item.refExcel || '',
          Estado: item.status,
          'ID Ingreso': item.dbData ? item.dbData.id_ingreso : '',
          'Alumno': item.dbData ? item.dbData.alumno : '',
          'Clave Rastreo BD': item.dbData ? item.dbData.aut_clave_rastreo : '',
          'Folio Operacion BD': item.dbData ? item.dbData.folio_operacion : ''
        })));

        // Crear libro
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Resultados');

        // Generar archivo y descargar
        const date = new Date().toISOString().split('T')[0];
        XLSX.writeFile(workbook, `validacion_ingresos_${date}.xlsx`);

        this.showSnackbar('Archivo exportado correctamente', 'success');
      } catch (error) {
        console.error('Error al exportar:', error);
        this.showSnackbar('Error al exportar los resultados', 'error');
      }
    },

    getBancoColor(banco) {
      if (!banco) return 'grey';

      const bancoUpper = banco.toString().toUpperCase();
      if (bancoUpper.includes('BBVA') || bancoUpper.includes('BANCOMER')) return 'blue darken-1';
      if (bancoUpper.includes('BR') || bancoUpper.includes('BANREGIO')) return 'purple';
      if (bancoUpper.includes('SANTANDER')) return 'red darken-1';
      if (bancoUpper.includes('BANAMEX') || bancoUpper.includes('CITI')) return 'blue-grey';
      if (bancoUpper.includes('BANORTE')) return 'orange darken-1';
      if (bancoUpper.includes('HSBC')) return 'red';

      return 'green'; // Para otros bancos
    },


    getTipoArchivo(tipo) {
      if (!tipo) return 'grey';

      const bancoUpper = tipo.toString().toUpperCase();
      if (bancoUpper.includes('EDO CUENTA') || bancoUpper.includes('BANCOMER')) return 'green';
      if (bancoUpper.includes('TPV') || bancoUpper.includes('BANREGIO')) return 'purple';
      if (bancoUpper.includes('COMERCIO ELECTRONICO')) return 'yellow';

      return 'green'; // Para otros bancos
    },

    getBancoLabel(banco) {
      if (!banco) return 'N/A';

      const bancoUpper = banco.toString().toUpperCase();
      if (bancoUpper.includes('BBVA') || bancoUpper.includes('BANCOMER')) return 'BBVA';
      if (bancoUpper.includes('BR') || bancoUpper.includes('BANREGIO')) return 'BanRegio';
      if (bancoUpper.includes('SANTANDER')) return 'Santander';
      if (bancoUpper.includes('BANAMEX') || bancoUpper.includes('CITI')) return 'Banamex';
      if (bancoUpper.includes('BANORTE')) return 'Banorte';
      if (bancoUpper.includes('HSBC')) return 'HSBC';

      return banco; // Para otros bancos, mostrar el valor original
    },

    // Función para guardar y enviar el archivo de Excel al backend
    async getCuentas() {
      this.cargandoCuentas = true
      try {
        const response = await this.$http.get('admin.cuentas.list')
        this.cuentas = response.data || []
      } catch (error) {
        this.cuentas = []
        const msg = error.response.data.message || 'Error al cargar las cuentas.'
        this.validarError(msg)
      } finally {
        this.cargandoCuentas = false
      }
    },

    async actualizarPreviewExcel() {
      this.previewExcel = null
      this.errorPreview = ''
      const seq = ++this.previewSeq

      if (!this.excelIngresos || !this.bancoArchivo || !this.tipoArchivo) {
        this.cargandoPreview = false
        return
      }

      this.cargandoPreview = true
      const formData = new FormData()
      formData.append('file', this.excelIngresos)
      formData.append('bancoArchivo', this.bancoArchivo)
      formData.append('tipoArchivo', this.tipoArchivo)

      try {
        const response = await this.$http.post('lectura.excel.ingresos.preview', formData)
        // Ignora respuestas de una selección anterior (archivo/banco/tipo cambiaron).
        if (seq !== this.previewSeq) return
        this.previewExcel = response && response.data ? response.data : null
      } catch (error) {
        if (seq !== this.previewSeq) return
        const data = error && error.response && error.response.data
        this.errorPreview = (data && (data.message || data.error)) || 'No se pudo leer el archivo para la vista previa.'
      } finally {
        if (seq === this.previewSeq) this.cargandoPreview = false
      }
    },

    saveExcel() {
      // 🔹 1. Validaciones principales
      // Validamos que todos los datos requeridos estén llenos
      if (!this.bancoArchivo || !this.tipoArchivo || !this.excelIngresos || !this.opEscuela) {
        this.validarErrorDirecto('Favor de llenar todos los datos y adjuntar el archivo antes de enviar.');
        return;
      }
      if (!this.idcuentas_escuela) {
        this.validarErrorDirecto('Favor de seleccionar una cuenta de banco.');
        return;
      }
      if (this.motivoBloqueoGuardar) {
        this.validarErrorDirecto(this.motivoBloqueoGuardar);
        return;
      }

      this.dialogConfirmarSubida = true;
    },

    async subirExcel() {
      this.dialogConfirmarSubida = false;

      // 🔹 2. Iniciamos el loader
      this.cargar = true;

      // 🔹 3. Construimos el FormData para enviar el archivo por POST
      const formData = new FormData();
      formData.append('file', this.excelIngresos);      // Archivo Excel seleccionado
      formData.append('bancoArchivo', this.bancoArchivo); // Banco seleccionado
      formData.append('tipoArchivo', this.tipoArchivo);   // Tipo de archivo seleccionado
      formData.append('opEscuela', this.opEscuela);   // Tipo de archivo seleccionado
      formData.append('idcuentas_escuela', this.idcuentas_escuela); // Cuenta de banco seleccionada

      try {
        // 🔹 4. Enviamos al endpoint que procesa el Excel
        const response = await this.$http.post('lectura.excel.ingresos', formData);
        const data = response && response.data ? response.data : {};

        // 🔹 5. Limpiamos el input de archivo (y opcionalmente filtros)
        this.excelIngresos = null;
        this.bancoArchivo = null;
        this.tipoArchivo  = null;
        this.opEscuela  = null;
        this.idcuentas_escuela = null;

        // 🔹 6. Mostramos mensaje de éxito
        const baseMsg = data.message || 'Archivo procesado correctamente.';
        const successMsg = data.registrosInsertados != null
          ? `${baseMsg} Registros insertados: ${data.registrosInsertados}.`
          : baseMsg;
        this.validarSuccess(successMsg);

        // 🔹 7. Refrescamos la tabla de ingresos
        this.getReportesBancos( );

      } catch (error) {
        // 🔹 8. Manejo seguro del error (sin optional chaining)
        let msg = 'Ocurrió un error al procesar el archivo. Intente de nuevo.';

        if (
          error &&
          error.response &&
          error.response.data &&
          error.response.data.message
        ) {
          msg = error.response.data.message;
        }

        this.validarError(msg);

      } finally {
        // 🔹 9. Apagamos el loader siempre
        this.cargar = false;
      }
    },

    async getReportesBancos( ){

      if( !this.fechaini || !this.fechafin ){
        this.validarError('Selecciona fecha de inicio y fecha final')
        return
      }

      if( this.fechaini > this.fechafin ){
        this.validarError('La fecha de inicio no puede ser mayor que la fecha final')
        return
      }

      this.cargar = true
      
      this.archivosLeidos = []
      // /*Initialize the form data*/

      try {

        const payload = {
          fechaini: this.fechaini,
          fechafin: this.fechafin
        }

        const response = await this.$http.post('reportes.bancos', payload );

        this.archivosLeidos     = response.data.archivos
        this.archivosFaltantes  = this.normalizarArchivosFaltantes( response.data.faltantes )

      } catch (error) {
        // 🔹 8. Manejo seguro del error (sin optional chaining)
        let msg = 'Ocurrió un error al procesar el archivo. Intente de nuevo.';

        if (
          error &&
          error.response &&
          error.response.data &&
          error.response.data.message
        ) {
          msg = error.response.data.message;
        }

        this.validarError(msg);

      } finally {
        // 🔹 9. Apagamos el loader siempre
        this.cargar = false;
      }
    },

    async getArchivosFaltantes( ){

      if( !this.fechainiA || !this.fechafinA ){
        this.validarError('Selecciona fecha de inicio y fecha final')
        return
      }

      this.cargar = true

      
      try {

        const payload = {
          fechaini: this.fechainiA,
          fechafin: this.fechafinA
        }

        const response = await this.$http.post('reportes.bancos.faltantes', payload );

        this.archivosFaltantes  = this.normalizarArchivosFaltantes( response.data.faltantes )

      } catch (error) {
        // 🔹 8. Manejo seguro del error (sin optional chaining)
        let msg = 'Ocurrió un error al procesar el archivo. Intente de nuevo.';

        if (
          error &&
          error.response &&
          error.response.data &&
          error.response.data.message
        ) {
          msg = error.response.data.message;
        }

        this.validarError(msg);

      } finally {
        // 🔹 9. Apagamos el loader siempre
        this.cargar = false;
      }
    },

    abrirArchivosSubidos () {
      if ( !this.fechainiArchivos ) this.fechainiArchivos = this.fechaini
      if ( !this.fechafinArchivos ) this.fechafinArchivos = this.fechafin
      this.dialogArchivosSubidos = true
      if ( this.fechainiArchivos && this.fechafinArchivos ) {
        this.getArchivosSubidos()
      }
    },

    async getArchivosSubidos () {
      if ( !this.fechainiArchivos || !this.fechafinArchivos ) {
        return this.validarErrorDirecto('Selecciona el rango de fechas de subida')
      }
      this.cargandoArchivosSubidos = true
      try {
        const response = await this.$http.post('estado.cuenta.archivos', {
          fechaini: this.fechainiArchivos,
          fechafin: this.fechafinArchivos,
        })
        this.archivosSubidos = Array.isArray(response.data) ? response.data : []
      } catch (error) {
        this.validarError(error)
      } finally {
        this.cargandoArchivosSubidos = false
      }
    },

    async descargarArchivoSubido (item) {
      if ( !item || !item.archivo_disponible ) return
      this.descargandoArchivoId = item.idexcel_ingresos
      try {
        const response = await this.$http.get(`estado.cuenta.archivo/${item.idexcel_ingresos}`, {
          responseType: 'blob',
        })
        const url = URL.createObjectURL(response.data)
        const link = document.createElement('a')
        link.href = url
        link.download = item.excelname || `estado-cuenta-${item.idexcel_ingresos}.xlsx`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        setTimeout(() => URL.revokeObjectURL(url), 1000)
      } catch (error) {
        let msg = 'No se pudo descargar el archivo'
        const data = error && error.response && error.response.data
        if (data instanceof Blob) {
          try {
            const json = JSON.parse(await data.text())
            if (json && json.message) msg = json.message
          } catch (e) { /* respuesta no JSON */ }
        } else if (data && data.message) {
          msg = data.message
        }
        this.validarErrorDirecto(msg)
      } finally {
        this.descargandoArchivoId = null
      }
    },

    abrirArchivosFaltantes () {
      if ( !this.fechainiA ) this.fechainiA = this.fechaini
      if ( !this.fechafinA ) this.fechafinA = this.fechafin
      this.reporteArchivosFaltantes = true
      if ( this.fechainiA && this.fechafinA ) {
        this.getArchivosFaltantes()
      }
    },

    claseFilaArchivoFaltante ( item ) {
      return this.esFinDeSemana( item.fecha ) ? 'faltantes-row--fin' : ''
    },

    normalizarArchivosFaltantes ( lista ) {
      return ( Array.isArray( lista ) ? lista : [] ).map( ( item ) => ( {
        ...item,
        fecha: this.fmtFechaKey( item.fecha ),
        BANREGIO_EDO_CUENTA: this.normalizarEstatusArchivo( item.BANREGIO_EDO_CUENTA ),
        BBVA_EDO_CUENTA: this.normalizarEstatusArchivo( item.BBVA_EDO_CUENTA ),
        HSBC_EDO_CUENTA: this.normalizarEstatusArchivo( item.HSBC_EDO_CUENTA ),
        HSBC_TPV: this.normalizarEstatusArchivo( item.HSBC_TPV ),
      } ) )
    },

    normalizarEstatusArchivo ( valor ) {
      const texto = String( valor || '' ).trim().toUpperCase()
      return texto === 'SI' ? 'SI' : 'NO'
    },

    esFinDeSemana ( fecha ) {
      const key = this.fmtFechaKey( fecha )
      if ( !key ) return false
      const dia = new Date( `${ key }T12:00:00` ).getDay()
      return dia === 0 || dia === 6
    },

    nombreDiaSemana ( fecha ) {
      if ( !fecha ) return ''
      const dias = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado']
      const key = this.fmtFechaKey( fecha )
      return dias[new Date( `${ key }T12:00:00` ).getDay()]
    },

    aplicaNAFinDeSemana ( item, campo ) {
      const valor = this.normalizarEstatusArchivo( item[campo] )
      return (
        this.columnasFinSemanaNA.includes( campo ) &&
        this.esFinDeSemana( item.fecha ) &&
        valor !== 'SI'
      )
    },

    estadoArchivoCelda ( item, campo ) {
      if ( this.aplicaNAFinDeSemana( item, campo ) ) {
        return {
          texto: 'NA',
          color: 'blue-grey lighten-4',
          textColor: 'blue-grey darken-2',
          outlined: true,
          icon: 'mdi-calendar-remove-outline',
          esNA: true,
        }
      }

      const valor = this.normalizarEstatusArchivo( item[campo] )
      if ( valor === 'SI' ) {
        return { texto: 'SI', color: 'green darken-1', textColor: 'white', outlined: false, icon: null, esNA: false }
      }
      return { texto: 'NO', color: 'red darken-1', textColor: 'white', outlined: false, icon: null, esNA: false }
    },

    chipEstadoArchivo ( item, campo ) {
      const estado = this.estadoArchivoCelda( item, campo )
      return {
        color: estado.color,
        textColor: estado.textColor,
        outlined: estado.outlined,
        esNA: estado.esNA,
        icon: estado.icon,
      }
    },

  }
};
</script>

<style scoped>
.estado-cuentas {
  background: #f5f7fa;
  min-height: 100%;
}

.page-header__icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 14px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

.page-header__title {
  font-size: 1.35rem;
  font-weight: 600;
  color: #1a1f36;
  letter-spacing: -0.02em;
  line-height: 1.2;
}

.page-header__subtitle {
  font-size: 0.8rem;
  color: #8892a6;
  margin-top: 2px;
}

.kpi-card {
  background: #fff;
  border-radius: 14px;
  padding: 16px 18px;
  height: 100%;
  border: 1px solid #eef1f6;
  transition: box-shadow 0.2s ease;
}

.kpi-card:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
}

.kpi-card__icon-wrap {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: #f5f7fa;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 10px;
}

.kpi-card__label {
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #8892a6;
}

.kpi-card__value {
  font-size: 1.15rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  margin-top: 4px;
  line-height: 1.2;
}

.kpi-card__meta {
  font-size: 0.72rem;
  color: #a0aec0;
  margin-top: 6px;
}

.breakdown-card {
  background: #fff;
  border-radius: 14px;
  border: 1px solid #eef1f6;
  overflow: hidden;
  height: 100%;
}

.breakdown-card__header {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  font-weight: 600;
  font-size: 0.85rem;
  color: #1a1f36;
  border-bottom: 1px solid #f0f2f7;
  background: #fafbfc;
}

.breakdown-card__body {
  display: flex;
  padding: 14px 16px;
  gap: 8px;
}

.breakdown-stat {
  flex: 1;
  text-align: center;
}

.breakdown-stat__label {
  display: block;
  font-size: 0.68rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #8892a6;
  margin-bottom: 4px;
}

.breakdown-stat__value {
  font-size: 0.95rem;
  font-weight: 700;
}

.bank-kpi {
  display: flex;
  align-items: center;
  background: #fff;
  border-radius: 14px;
  padding: 14px 16px;
  border: 1px solid #eef1f6;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  height: 100%;
}

.bank-kpi:hover {
  border-color: #c5d0e6;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.bank-kpi__avatar {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-right: 14px;
}

.bank-kpi__abbr {
  color: #fff;
  font-weight: 800;
  font-size: 0.85rem;
  letter-spacing: 0.04em;
}

.bank-kpi__name {
  font-weight: 600;
  font-size: 0.9rem;
  color: #1a1f36;
}

.bank-kpi__amounts {
  font-size: 0.82rem;
  font-weight: 600;
  margin-top: 2px;
}

.bank-kpi__sep {
  color: #cbd5e0;
  margin: 0 4px;
}

.bank-kpi__meta {
  font-size: 0.7rem;
  color: #a0aec0;
  margin-top: 2px;
}

.bank-kpi__active {
  position: absolute;
  top: 10px;
  right: 10px;
}

.table-card {
  background: #fff;
  border: 1px solid #eef1f6;
  overflow: hidden;
}

.table-card__toolbar {
  border-bottom: 1px solid #f0f2f7;
  background: #fafbfc;
}

.estado-table >>> thead th {
  font-size: 0.72rem !important;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #8892a6 !important;
  font-weight: 600 !important;
  background: #fafbfc !important;
  border-bottom: 1px solid #eef1f6 !important;
}

.estado-table >>> tbody tr:hover {
  background: #f8fafc !important;
}

.bank-avatar {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 0.7rem;
  font-weight: 800;
  letter-spacing: 0.03em;
}

.escuela-chip,
.tipo-chip {
  font-weight: 500;
}

.money-cell {
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.fecha-cell {
  font-size: 0.875rem;
  color: #4a5568;
}

.text-success {
  color: #2e7d32 !important;
}

.text-error {
  color: #c62828 !important;
}

.filters-bar {
  background: #fff;
  border: 1px solid #eef1f6;
}

.ie-card {
  background: #fff;
  border: 1px solid #eef1f6;
  overflow: hidden;
}

.ie-card__header {
  border-bottom: 1px solid #eef1f6;
  background: #fafbfc;
}

.ie-table-wrap {
  overflow-x: auto;
  max-width: 100%;
}

.ie-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 0.78rem;
  min-width: 1200px;
}

.ie-th,
.ie-td {
  padding: 8px 10px;
  border-bottom: 1px solid #e8ecf1;
  border-right: 1px solid #e8ecf1;
  white-space: nowrap;
}

.ie-th {
  font-weight: 700;
  text-align: center;
  font-size: 0.68rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  position: sticky;
  top: 0;
  z-index: 2;
}

.ie-th--group {
  top: 0;
  z-index: 3;
  color: #fff;
  font-size: 0.72rem;
}

.ie-th--ingresos { background: #2e7d32; }
.ie-th--egresos { background: #e65100; top: 28px; }
.ie-th--saldos { background: #f57c00; top: 28px; }

.ie-table__row-meta .ie-th--meta {
  top: 0;
  background: #f5f7fa;
  z-index: 3;
}

.ie-table thead tr:nth-child(2) .ie-th {
  top: 28px;
}

.ie-th--ing { background: #e8f5e9; color: #1b5e20; }
.ie-th--eg { background: #fff3e0; color: #e65100; }
.ie-th--sal { background: #fff8e1; color: #f57f17; }
.ie-th--total { font-weight: 800; }
.ie-th--fecha, .ie-th--fecha2 { background: #f5f7fa; text-align: left; min-width: 140px; }
.ie-th--mini { background: #f5f7fa; min-width: 42px; }

.ie-td--sticky {
  position: sticky;
  left: 0;
  z-index: 1;
  background: #fff;
}

.ie-td--fecha { left: 72px; min-width: 200px; }
.ie-td--mini { left: 272px; text-align: center; }
.ie-td--escuela { left: 0; min-width: 72px; }

.ie-td--money {
  text-align: right;
  font-variant-numeric: tabular-nums;
  min-width: 88px;
}

.ie-td--ing { background: #f9fdf9; }
.ie-td--eg { background: #fffaf5; }
.ie-td--sal { background: #fffdf5; }
.ie-td--total { font-weight: 600; }

.ie-table__footer .ie-td {
  background: #f0f4f8;
  border-top: 2px solid #cfd8dc;
  font-weight: 700;
}

.ie-table tbody tr:hover .ie-td {
  filter: brightness(0.98);
}

.faltantes-dialog__header {
  position: sticky;
  top: 0;
  z-index: 2;
}

.faltantes-leyenda {
  font-size: 0.875rem;
  line-height: 1.45;
}

.faltantes-table >>> thead th {
  background: #f5f7fa !important;
  font-size: 0.72rem !important;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #546e7a !important;
}

.faltantes-table >>> tbody td {
  vertical-align: middle;
}

.faltantes-table >>> tr.faltantes-row--fin {
  background: #f3f5fb !important;
}

.faltantes-chip {
  min-width: 52px;
  justify-content: center;
}

.faltantes-chip--na {
  border-style: dashed !important;
  font-weight: 600;
  letter-spacing: 0.04em;
}

.faltantes-fecha {
  display: flex;
  flex-direction: column;
  line-height: 1.2;
}

.faltantes-fecha__dia {
  font-size: 0.72rem;
  color: #78909c;
}

.faltantes-fecha__dia--fin {
  color: #5c6bc0;
  font-weight: 600;
}

.faltantes-resumen .v-chip {
  font-weight: 600;
}
</style>
